import { useEffect, useState } from 'react'
import { ClipboardList, ExternalLink, Plus, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { api } from '@/lib/api'

interface FormItem {
  id: string
  name: string
  publicSlug: string
  fieldsJson: unknown
  mappingsToClientJson: unknown
  tagsToApply: string[]
  consentRequired: boolean
  consentText: string | null
  active: boolean
  updatedAt: string
  _count: { submissions: number }
}

interface Submission {
  id: string
  dataJson: Record<string, unknown>
  consentEmail: boolean
  consentSms: boolean
  consentWhatsapp: boolean
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  referrer: string | null
  submittedAt: string
  clientStub: { id: string; displayName: string; email: string | null; phoneE164: string | null } | null
}

const EXAMPLE_FIELDS = JSON.stringify(
  [
    { key: 'name', label: 'Nombre', type: 'text', required: true },
    { key: 'email', label: 'Email', type: 'email', required: true },
    { key: 'phone', label: 'Teléfono', type: 'tel' },
    { key: 'message', label: 'Mensaje', type: 'textarea', required: true },
  ],
  null,
  2,
)

const EXAMPLE_MAPPINGS = JSON.stringify(
  { name: 'displayName', email: 'email', phone: 'phoneE164' },
  null,
  2,
)

export function FormsPage() {
  const [items, setItems] = useState<FormItem[]>([])
  const [selected, setSelected] = useState<FormItem | null>(null)
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [draftName, setDraftName] = useState('')
  const [draftSlug, setDraftSlug] = useState('')
  const [draftFields, setDraftFields] = useState(EXAMPLE_FIELDS)
  const [draftMappings, setDraftMappings] = useState(EXAMPLE_MAPPINGS)
  const [draftTags, setDraftTags] = useState('')
  const [draftConsentText, setDraftConsentText] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function load() {
    const r = await api<{ items: FormItem[] }>('/v1/marketing/forms')
    setItems(r.items)
  }

  useEffect(() => {
    void load()
  }, [])

  async function pick(f: FormItem) {
    setSelected(f)
    setDraftName(f.name)
    setDraftSlug(f.publicSlug)
    setDraftFields(JSON.stringify(f.fieldsJson, null, 2))
    setDraftMappings(JSON.stringify(f.mappingsToClientJson, null, 2))
    setDraftTags(f.tagsToApply.join(', '))
    setDraftConsentText(f.consentText ?? '')
    setErr(null)
    try {
      const r = await api<{ items: Submission[] }>(`/v1/marketing/forms/${f.id}/submissions`)
      setSubmissions(r.items)
    } catch {
      setSubmissions([])
    }
  }

  function newOne() {
    setSelected(null)
    setDraftName('')
    setDraftSlug('')
    setDraftFields(EXAMPLE_FIELDS)
    setDraftMappings(EXAMPLE_MAPPINGS)
    setDraftTags('')
    setDraftConsentText('')
    setSubmissions([])
    setErr(null)
  }

  async function save() {
    setBusy(true)
    setErr(null)
    try {
      const body = JSON.stringify({
        name: draftName,
        publicSlug: draftSlug,
        fieldsJson: JSON.parse(draftFields),
        mappingsToClientJson: JSON.parse(draftMappings),
        tagsToApply: draftTags
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        consentText: draftConsentText.trim() || undefined,
      })
      if (selected) {
        await api(`/v1/marketing/forms/${selected.id}`, { method: 'PATCH', body })
      } else {
        await api('/v1/marketing/forms', { method: 'POST', body })
      }
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function remove(f: FormItem) {
    if (!confirm(`¿Eliminar el form "${f.name}"?`)) return
    await api(`/v1/marketing/forms/${f.id}`, { method: 'DELETE' })
    if (selected?.id === f.id) newOne()
    await load()
  }

  return (
    <div className="p-6 max-w-6xl">
      <header className="mb-4">
        <h1 className="text-lg font-medium tracking-tight flex items-center gap-2">
          <ClipboardList className="size-5 text-blueprint-700" />
          Forms públicos
        </h1>
        <p className="text-xs font-mono text-ink-700/60">
          captura UTMs + consent granular · disponible en{' '}
          <span className="font-medium">/p/forms/&lt;slug&gt;</span>
        </p>
      </header>

      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-4 bp-card overflow-hidden">
          <div className="px-3 py-2 border-b border-ink-900/10 flex items-center justify-between">
            <span className="text-xs font-mono uppercase tracking-wider text-ink-700/60">
              forms
            </span>
            <button onClick={newOne} className="bp-btn" type="button">
              <Plus className="size-3" /> Nuevo
            </button>
          </div>
          <ul>
            {items.map((f) => (
              <li key={f.id}>
                <button
                  type="button"
                  onClick={() => void pick(f)}
                  className={[
                    'w-full text-left px-3 py-2 border-b border-ink-900/5 hover:bg-surface-alt',
                    selected?.id === f.id ? 'bg-blueprint-50' : '',
                  ].join(' ')}
                >
                  <div className="text-sm font-medium">{f.name}</div>
                  <div className="text-[10px] font-mono text-ink-700/60">
                    /p/forms/{f.publicSlug} · {f._count.submissions} envíos
                  </div>
                </button>
              </li>
            ))}
            {items.length === 0 && (
              <li className="px-3 py-6 text-center text-xs text-ink-700/50">sin forms</li>
            )}
          </ul>
        </div>

        <div className="col-span-8 space-y-4">
          <div className="bp-card p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                  nombre
                </span>
                <input
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                  slug público
                </span>
                <input
                  value={draftSlug}
                  onChange={(e) => setDraftSlug(e.target.value.toLowerCase())}
                  className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm font-mono"
                />
              </label>
            </div>
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                campos (JSON)
              </span>
              <textarea
                value={draftFields}
                onChange={(e) => setDraftFields(e.target.value)}
                spellCheck={false}
                className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-xs font-mono min-h-[140px]"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                mapeos a ClientStub (JSON)
              </span>
              <textarea
                value={draftMappings}
                onChange={(e) => setDraftMappings(e.target.value)}
                spellCheck={false}
                className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-xs font-mono min-h-[80px]"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                tags a aplicar (IDs separados por coma)
              </span>
              <input
                value={draftTags}
                onChange={(e) => setDraftTags(e.target.value)}
                className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-xs font-mono"
                placeholder="tag-web, tag-warm"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                texto de consent (LSSI)
              </span>
              <textarea
                value={draftConsentText}
                onChange={(e) => setDraftConsentText(e.target.value)}
                className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-xs min-h-[60px]"
              />
            </label>
            {err && (
              <div className="text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                {err}
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void save()}
                className="bp-btn-primary"
                disabled={busy || !draftName.trim() || !draftSlug.trim()}
              >
                {selected ? 'Guardar' : 'Crear form'}
              </button>
              {selected && (
                <>
                  <a
                    href={`/p/forms/${selected.publicSlug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="bp-btn"
                  >
                    <ExternalLink className="size-3" /> Abrir público
                  </a>
                  <button
                    type="button"
                    onClick={() => void remove(selected)}
                    className="bp-btn text-red-700 ml-auto"
                  >
                    <Trash2 className="size-3" /> Eliminar
                  </button>
                </>
              )}
            </div>
          </div>

          {selected && submissions.length > 0 && (
            <div className="bp-card overflow-hidden">
              <div className="px-3 py-2 border-b border-ink-900/10 flex items-center justify-between">
                <span className="text-xs font-mono uppercase tracking-wider text-ink-700/60">
                  envíos recientes
                </span>
                <span className="text-[10px] font-mono text-ink-700/40">
                  últimos {submissions.length}
                </span>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-surface-alt">
                  <tr className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
                    <th className="px-3 py-1.5 text-left">Cliente</th>
                    <th className="px-3 py-1.5 text-left">Consent</th>
                    <th className="px-3 py-1.5 text-left">UTM</th>
                    <th className="px-3 py-1.5 text-right">Cuándo</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((s) => (
                    <tr key={s.id} className="border-t border-ink-900/5">
                      <td className="px-3 py-1.5">
                        {s.clientStub ? (
                          <Link
                            to={`/marketing/clients/${s.clientStub.id}`}
                            className="text-blueprint-700"
                          >
                            {s.clientStub.displayName}
                          </Link>
                        ) : (
                          <span className="text-ink-700/40">—</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 font-mono">
                        {s.consentEmail && 'e '}
                        {s.consentSms && 's '}
                        {s.consentWhatsapp && 'w'}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-ink-700/70">
                        {s.utmSource ?? '—'}
                        {s.utmCampaign ? ` / ${s.utmCampaign}` : ''}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-ink-700/60">
                        {new Date(s.submittedAt).toLocaleString('es-ES')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
